import { ShaderProgram } from '../renderer/ShaderProgram';
import { InstancedMesh } from '../renderer/InstancedMesh';
import type { Camera } from '../renderer/Camera';
import type { Renderable } from '../renderer/RenderPipeline';
import type { StellarRenderConfig } from '../config/StellarRenderConfig';
import type { ClusterContent } from '../network/ContentLoader';
import type { SunLifecycleEntry } from '../systems/SunLifecycleTypes';
import { axialToWorld } from '../math/HexMath';
import { createIcoSphere, createQuad } from '../renderer/GeometryFactory';

import vertSrc from '../shaders/sun.vert.glsl?raw';
import prefixSrc from '../shaders/glsl-prefix.glsl?raw';
import noiseSrc from '../shaders/noise3d.glsl?raw';
import fragSrc from '../shaders/sun.frag.glsl?raw';
import glowVertSrc from '../shaders/sun-glow.vert.glsl?raw';
import glowFragSrc from '../shaders/sun-glow.frag.glsl?raw';

const FLOATS_PER_INSTANCE = 7;
const SPECTRAL_INDEX: Record<string, number> = { M: 0, K: 1, G: 2, F: 3, O: 4 };

export class SunRenderer implements Renderable {
  private _sphereShader: ShaderProgram;
  private _glowShader: ShaderProgram;
  private _sphereMesh: InstancedMesh;
  private _glowMesh: InstancedMesh;
  private _config: StellarRenderConfig;
  private _hexRadius: number;
  private _sunCount = 0;
  private _startTime = performance.now();
  private _instanceBuffer: Float32Array;
  private _lifecycleData: ReadonlyMap<string, SunLifecycleEntry> | null = null;

  constructor(
    gl: WebGL2RenderingContext,
    config: StellarRenderConfig,
    hexRadius: number,
    maxInstances: number,
  ) {
    this._config = config;
    this._hexRadius = hexRadius;
    this._instanceBuffer = new Float32Array(maxInstances * FLOATS_PER_INSTANCE);

    const instanceAttribs = [
      { name: 'a_worldPos', size: 3, divisor: 1 },
      { name: 'a_spectralClass', size: 1, divisor: 1 },
      { name: 'a_size', size: 1, divisor: 1 },
      { name: 'a_scaleMul', size: 1, divisor: 1 },
      { name: 'a_age', size: 1, divisor: 1 },
    ];

    const combinedFrag = prefixSrc + noiseSrc + '\n' + fragSrc;
    this._sphereShader = new ShaderProgram(gl, vertSrc, combinedFrag);
    const sphere = createIcoSphere(4);
    const geoData = this._interleaveSphere(sphere.positions, sphere.normals);
    this._sphereMesh = new InstancedMesh(
      gl, this._sphereShader, geoData,
      [{ name: 'a_position', size: 3, divisor: 0 }, { name: 'a_normal', size: 3, divisor: 0 }],
      instanceAttribs, sphere.indices,
    );

    this._glowShader = new ShaderProgram(gl, glowVertSrc, glowFragSrc);
    const quad = createQuad();
    this._glowMesh = new InstancedMesh(
      gl, this._glowShader, quad.positions,
      [{ name: 'a_corner', size: 2, divisor: 0 }],
      instanceAttribs,
    );

    this._uploadUniforms(gl);
  }

  /** Lifecycle-Daten setzen. Wird von ContentManager bei Updates aufgerufen. */
  setLifecycleData(data: ReadonlyMap<string, SunLifecycleEntry>): void {
    this._lifecycleData = data;
  }

  updateFromContent(contents: ClusterContent[]): void {
    let count = 0;
    const max = this._instanceBuffer.length / FLOATS_PER_INSTANCE;
    const lc = this._lifecycleData;

    for (let c = 0; c < contents.length; c++) {
      const stars = contents[c].stars;
      for (let i = 0; i < stars.length; i++) {
        if (count >= max) break;
        const s = stars[i];
        if (s.axial_q == null || s.axial_r == null) continue;
        const [wx, wz] = axialToWorld(s.axial_q, s.axial_r, this._hexRadius);
        const idx = count * FLOATS_PER_INSTANCE;
        const size = (s.scale ?? 1.0) * this._config.sunBaseSize;
        const entry = lc?.get(s.entity_id ?? '');
        this._instanceBuffer[idx] = wx;
        this._instanceBuffer[idx + 1] = size;
        this._instanceBuffer[idx + 2] = wz;
        this._instanceBuffer[idx + 3] = SPECTRAL_INDEX[s.spectral_class ?? 'G'] ?? 2;
        this._instanceBuffer[idx + 4] = size;
        this._instanceBuffer[idx + 5] = entry?.scaleMul ?? 1.0;
        this._instanceBuffer[idx + 6] = entry?.age ?? 0.0;
        count++;
      }
    }
    this._sunCount = count;
    const sub = this._instanceBuffer.subarray(0, count * FLOATS_PER_INSTANCE);
    this._sphereMesh.updateInstances(sub, count);
    this._glowMesh.updateInstances(sub, count);
  }

  get sunCount(): number { return this._sunCount; }

  render(gl: WebGL2RenderingContext, camera: Camera): void {
    if (this._sunCount === 0) return;
    const time = (performance.now() - this._startTime) / 1000.0;

    gl.enable(gl.DEPTH_TEST);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
    gl.depthMask(false);

    this._glowShader.use();
    this._glowShader.setMat4('u_projection', camera.projection.data);
    this._glowShader.setMat4('u_view', camera.view.data);
    this._glowShader.setFloat('u_time', time);
    const right = this._cameraRight(camera);
    const up = this._cameraUp(camera);
    this._glowShader.setVec3('u_cameraRight', right[0], right[1], right[2]);
    this._glowShader.setVec3('u_cameraUp', up[0], up[1], up[2]);
    this._glowMesh.draw();

    gl.depthMask(true);
    gl.disable(gl.BLEND);

    this._sphereShader.use();
    this._sphereShader.setMat4('u_projection', camera.projection.data);
    this._sphereShader.setMat4('u_view', camera.view.data);
    this._sphereShader.setFloat('u_time', time);
    this._sphereShader.setVec3('u_cameraPos',
      camera.position.x, camera.position.y, camera.position.z);
    this._sphereMesh.draw();

    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
  }

  dispose(): void {
    this._sphereShader.dispose();
    this._glowShader.dispose();
    this._sphereMesh.dispose();
    this._glowMesh.dispose();
  }

  private _interleaveSphere(pos: Float32Array, norm: Float32Array): Float32Array {
    const count = pos.length / 3;
    const out = new Float32Array(count * 6);
    for (let i = 0; i < count; i++) {
      const i3 = i * 3, i6 = i * 6;
      out[i6] = pos[i3]; out[i6 + 1] = pos[i3 + 1]; out[i6 + 2] = pos[i3 + 2];
      out[i6 + 3] = norm[i3]; out[i6 + 4] = norm[i3 + 1]; out[i6 + 5] = norm[i3 + 2];
    }
    return out;
  }

  private _uploadUniforms(gl: WebGL2RenderingContext): void {
    const classes = ['M', 'K', 'G', 'F', 'O'];
    for (const shader of [this._sphereShader, this._glowShader]) {
      shader.use();
      for (let i = 0; i < 5; i++) {
        const info = this._config.spectralClasses[classes[i]];
        const loc = gl.getUniformLocation(shader.program, `u_spectralColors[${i}]`);
        if (loc) gl.uniform3f(loc, info.color[0], info.color[1], info.color[2]);
      }
    }
    this._sphereShader.use();
    for (let i = 0; i < 5; i++) {
      const info = this._config.spectralClasses[classes[i]];
      const cLoc = gl.getUniformLocation(this._sphereShader.program, `u_coronaScales[${i}]`);
      if (cLoc) gl.uniform1f(cLoc, info.coronaScale);
      const nLoc = gl.getUniformLocation(this._sphereShader.program, `u_noiseScales[${i}]`);
      if (nLoc) gl.uniform1f(nLoc, info.noiseScale);
      const sLoc = gl.getUniformLocation(this._sphereShader.program, `u_speeds[${i}]`);
      if (sLoc) gl.uniform1f(sLoc, info.speed);
    }
  }

  private _right = new Float32Array(3);
  private _up = new Float32Array(3);

  private _cameraRight(cam: Camera): Float32Array {
    const v = cam.view.data;
    this._right[0] = v[0]; this._right[1] = v[4]; this._right[2] = v[8];
    return this._right;
  }

  private _cameraUp(cam: Camera): Float32Array {
    const v = cam.view.data;
    this._up[0] = v[1]; this._up[1] = v[5]; this._up[2] = v[9];
    return this._up;
  }
}
